import { api } from '../api/client';
import { ResourceList } from '../components/ResourceList';
import { useT } from '../i18n/I18nProvider';

interface RelayHealth {
  reachable: boolean;
  accepting: boolean;
  pairings: number;
  bridgedPairs: number;
  maxPairings: number;
  latencyMs: number | null;
  observedAt: string;
  bytesForwarded: string;
  usageGb: number;
  throughputBps: number;
  estimatedCostCents: number;
  utilization: number | null;
}

interface Relay {
  id: string;
  region: string;
  name: string;
  dataEndpoint: string;
  healthUrl: string;
  enabled: boolean;
  costPerGbCents: number;
  health: RelayHealth | null;
}

/** Human-readable byte rate: the relay reports bytes/sec, operators think in bits. */
function formatThroughput(bps: number): string {
  const bitsPerSec = bps * 8;
  if (bitsPerSec >= 1e9) return `${(bitsPerSec / 1e9).toFixed(1)} Gbps`;
  if (bitsPerSec >= 1e6) return `${(bitsPerSec / 1e6).toFixed(1)} Mbps`;
  if (bitsPerSec >= 1e3) return `${(bitsPerSec / 1e3).toFixed(1)} Kbps`;
  return `${Math.round(bitsPerSec)} bps`;
}

function formatUsage(gb: number): string {
  if (gb >= 1000) return `${(gb / 1000).toFixed(2)} TB`;
  return `${gb.toFixed(2)} GB`;
}

const formatCost = (cents: number): string => `$${(cents / 100).toFixed(2)}`;

export function RelaysPage(): JSX.Element {
  const t = useT();

  /** The status cell: a relay's reachability and whether it is taking sessions. */
  const status = (r: Relay): JSX.Element => {
    if (!r.health) return <span className="chip chip--warning">{t('relay.notPolled')}</span>;
    if (!r.health.reachable)
      return <span className="chip chip--failure">{t('relay.unreachable')}</span>;
    const accepting = r.health.accepting;
    return (
      <span className={`chip ${accepting ? 'chip--success' : 'chip--warning'}`}>
        {accepting ? t('relay.accepting') : t('relay.full')}
      </span>
    );
  };

  const capacity = (r: Relay): string => {
    if (!r.health || r.health.maxPairings === 0) return `${r.health?.pairings ?? 0}`;
    const pct =
      r.health.utilization !== null ? ` (${Math.round(r.health.utilization * 100)}%)` : '';
    return `${r.health.pairings}/${r.health.maxPairings}${pct}`;
  };

  return (
    <ResourceList<Relay>
      title={t('relay.title')}
      emptyHint={t('relay.emptyHint')}
      load={() => api.get<Relay[]>('/relays').then((items) => ({ items }))}
      columns={[
        { header: t('relay.region'), render: (r) => r.region },
        { header: t('relay.name'), render: (r) => r.name },
        { header: t('relay.status'), render: status },
        {
          header: t('relay.latency'),
          render: (r) =>
            r.health?.latencyMs !== null && r.health?.latencyMs !== undefined
              ? `${r.health.latencyMs} ms`
              : '—',
        },
        {
          header: t('relay.throughput'),
          render: (r) => (r.health ? formatThroughput(r.health.throughputBps) : '—'),
        },
        {
          header: t('relay.usage'),
          render: (r) => (r.health ? formatUsage(r.health.usageGb) : '—'),
        },
        {
          header: t('relay.cost'),
          render: (r) => (r.health ? formatCost(r.health.estimatedCostCents) : '—'),
        },
        { header: t('relay.capacity'), render: capacity },
      ]}
      create={{
        label: t('common.create'),
        fields: [
          { name: 'region', label: t('relay.region'), required: true, placeholder: 'us-east' },
          { name: 'name', label: t('relay.name'), required: true, placeholder: 'us-east-1' },
          {
            name: 'dataEndpoint',
            label: t('relay.dataEndpoint'),
            required: true,
            placeholder: 'relay1.example.net:8443',
          },
          {
            name: 'healthUrl',
            label: t('relay.healthUrl'),
            required: true,
            placeholder: 'http://relay1.example.net:8080',
          },
          { name: 'costPerGbCents', label: t('relay.costPerGb'), placeholder: '0' },
        ],
        submit: (v) =>
          api
            .post('/relays', {
              region: v.region,
              name: v.name,
              dataEndpoint: v.dataEndpoint,
              healthUrl: v.healthUrl,
              // The form is text; cost is a number the API validates.
              costPerGbCents: v.costPerGbCents ? Number(v.costPerGbCents) : 0,
            })
            .then(() => undefined),
      }}
      rowActions={(r, reload) => (
        <button
          type="button"
          className="btn btn--ghost btn--danger"
          onClick={() => {
            if (window.confirm(t('relay.confirmRetire'))) {
              void api.del(`/relays/${r.id}`).then(reload);
            }
          }}
        >
          {t('relay.retire')}
        </button>
      )}
    />
  );
}
